import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button } from 'primereact/button'
import { push } from 'redux-first-history'
import { validateProgrammeChanges, validateContributionPercentages, getContributionValidationDialogConfig, showContributionValidationDialog, showContributionDeletionConfirmation, validateContributionZeroValues, validateNonEligibleContributorTypes, hasOnlyNonEligibleContributionChanges, validateDuplicateContributions } from 'components/Body/data-entry/AudioVisual/validations'
import { confirmPopup } from 'primereact/confirmpopup'
import { confirmDialog } from 'primereact/confirmdialog'
import { SplitButton } from 'primereact/splitbutton'
import { selectEpisodeResultPresentation, selectEpisodeDraftHasDeletedTransmissions, selectEpisodeDraftHasDeletedContributions, selectAudioVisualResultHasChanges, selectIsNewEpisode, selectProgrammeResultHasChanges, selectProgrammeUrl, selectEpisodeDraftHasContributions, selectIsContributionRowEditing, selectEpisodeResultOriginal } from 'components/Body/data-entry/AudioVisual/store/selectors'
import { setIsNewEpisodeAction, setShowDataUpdateAction, setShowDataCreateAction, setDataUpdateKeyAction, setProgrammeCreateStatusAction, setProgrammeUpdateStatusAction, setEpisodeDraftResultsTransmissionsAction, clearEpisodeResultsTransmissionsFromDraftsAction, clearProgrammeDraftResultsAction, clearProgrammeDraftResultsEpisodesAction, clearProgrammeDraftResultsTitlesAction } from 'components/Body/data-entry/AudioVisual/store/actions'

// ReSharper disable once InconsistentNaming
export const ProgrammeDetailsFooter = ({ programmeGuid, isNewProgramme, toast, resultsDataForEdit, primaryTitle, episodePrimaryTitle, programmeTypeId, formatId, primaryCountry, episodeValidationRequired, isFilm, programmeDataOriginal }) => {
    const draftAvailable = useSelector(selectAudioVisualResultHasChanges)
    const draftProgrammeAvailable = useSelector(selectProgrammeResultHasChanges)
    const deleteTransmissionAvailable = useSelector(selectEpisodeDraftHasDeletedTransmissions)
    const deleteContributionAvailable = useSelector(selectEpisodeDraftHasDeletedContributions)
    var episodeData = useSelector(selectEpisodeResultPresentation)
    const episodeDataOriginal = useSelector(selectEpisodeResultOriginal)
    const isNewEpisodeFlag = useSelector(selectIsNewEpisode)
    const programmeUrl = useSelector(selectProgrammeUrl)
    const hasContributionChanges = useSelector(selectEpisodeDraftHasContributions)
    const isContributionEditing = useSelector(selectIsContributionRowEditing)

    const dispatch = useDispatch()
    if (isNewEpisodeFlag) { //TODO : with redux store
        if (episodeData && episodeData.id) {
            const episodesArray = Object.values(programmeDataOriginal?.episodes || {})
            const episodeExists = episodesArray.find(episode => episode.id === episodeData.id)
            if (episodeExists) {
                dispatch(setIsNewEpisodeAction(false))
            }
        }
    }
  
    const navigateTo = route => {
        dispatch(push(route)) // Dispatch action to navigate to the specified route
    }
    const clearEpisodeDraftResultsTransmissions = () => dispatch(clearEpisodeResultsTransmissionsFromDraftsAction())
    const setEpisodeDraftResultsTransmissions = (transmissions) => dispatch(setEpisodeDraftResultsTransmissionsAction(transmissions))
    const clearProgrammeDraftResults = () => dispatch(clearProgrammeDraftResultsAction())
    const clearProgrammeEpisodeDraftResults = () => dispatch(clearProgrammeDraftResultsEpisodesAction())
    const setShowDataUpdate = (show) => dispatch(setShowDataUpdateAction(show))
    const setShowDataCreate = (show) => dispatch(setShowDataCreateAction(show))
    const setDataUpdateKey = (key) => dispatch(setDataUpdateKeyAction(key))

    //On direct click of Accept in split button
    const splitSave = async (event) => {
        //transmission delete confirmation
        if (deleteTransmissionAvailable) {
            confirmPopup({
                target: event.currentTarget,
                message: 'You are about to delete Transmissions. Do you want to continue?',
                icon: 'pi pi-info-circle',
                acceptClassName: 'p-button-danger',
                accept: () => handleSaveAfterTransmissionConfirm(event),
                reject: rejectTransmissionChange
            })
        } else if (deleteContributionAvailable) {
            showContributionDeletionConfirmation(event, episodeData,
                episodeDataOriginal?.contributions || {}, acceptSave)
        } else if (hasContributionChanges) {
            // Validate contributions BEFORE showing confirmation dialog
            const contributionValidation = validateContributions()
            if (!contributionValidation.isValid) {
                // Show validation error dialog and block save
                showContributionValidationDialog(contributionValidation.dialogConfig)
                return
            }

            // Check if only Actor/Director contributions changed
            const onlyNonEligibleChanges = hasOnlyNonEligibleContributionChanges(
                episodeData,
                episodeDataOriginal?.contributions || {}
            )

            if (onlyNonEligibleChanges) {
                // Skip reapportionment confirmation for Actor/Director-only changes
                acceptSave()
            } else {
                // Show reapportionment confirmation for eligible contribution changes
                confirmDialog({
                    message: 'Any undistributed money on this episode will now be re-apportioned. Do you want to continue?',
                    header: 'Confirm Changes',
                    icon: 'pi pi-info-circle',
                    acceptClassName: 'p-button-warning',
                    accept: acceptSave
                })
            }
        } else {
            acceptSave()
        }
    }

    const handleSaveAfterTransmissionConfirm = (event) => {
        if (deleteContributionAvailable) {
            showContributionDeletionConfirmation(event, episodeData,
                episodeDataOriginal?.contributions || {}, acceptSave)
        } else if (hasContributionChanges) {
            // Validate contributions BEFORE showing confirmation dialog
            const contributionValidation = validateContributions()
            if (!contributionValidation.isValid) {
                // Show validation error dialog and block save
                showContributionValidationDialog(contributionValidation.dialogConfig)
                return
            }

            // Check if only Actor/Director contributions changed
            const onlyNonEligibleChanges = hasOnlyNonEligibleContributionChanges(
                episodeData,
                episodeDataOriginal?.contributions || {}
            )

            if (onlyNonEligibleChanges) {
                // Skip reapportionment confirmation for Actor/Director-only changes
                acceptSave()
            } else {
                // Show reapportionment confirmation for eligible contribution changes
                confirmDialog({
                    message: 'Any undistributed money on this episode will now be re-apportioned. Do you want to continue?',
                    header: 'Confirm Changes',
                    icon: 'pi pi-info-circle',
                    acceptClassName: 'p-button-warning',
                    accept: acceptSave
                })
            }
        } else {
            acceptSave()
        }
    }

    const acceptSave = async () => {
        const isValid = await validateProgrammeChanges(
            toast,
            dispatch,
            resultsDataForEdit,
            primaryTitle,
            programmeTypeId,
            formatId,
            primaryCountry,
            episodeData,
            episodePrimaryTitle,
            episodeValidationRequired,
            programmeGuid,
            programmeUrl
        )

        if (!isValid) {
            return
        }

        // Validate contributions don't have 0 percentage or minutes (except Actor/Director)
        const zeroValueValidation = validateContributionZeroValues(episodeData)
        if (!zeroValueValidation.isValid) {
            toast.current.show({ severity: 'error', summary: 'Validation Error', detail: zeroValueValidation.message })
            return
        }

        // Validate Non-Eligible Contributors have only Actor or Director contribution types
        // This is especially important for film types where episode is saved with programme
        const nonEligibleValidation = validateNonEligibleContributorTypes(episodeData)
        if (!nonEligibleValidation.isValid) {
            confirmDialog({
                message: nonEligibleValidation.message,
                header: 'Validation Error',
                icon: 'pi pi-exclamation-triangle',
                acceptLabel: 'OK',
                acceptClassName: 'p-button-warning',
                rejectVisible: false,
                accept: () => {}
            })
            return
        }

        // Validate contributions for films
        const contributionValidation = validateContributions()
        if (!contributionValidation.isValid) {
            showContributionValidationDialog(contributionValidation.dialogConfig)
            return
        }

        if (isNewProgramme) {
            handleCreateTrigger()
        } else {
            handleUpdateTrigger()
        }

    }

    const rejectTransmissionChange = () => {
        clearEpisodeDraftResultsTransmissions()
        dispatch(setEpisodeDraftResultsTransmissions(episodeData?.transmissions))

    }

    // Validate contributions for films and return validation result
    const validateContributions = () => {
        // Only validate for films with contribution changes or deletions
        if (!isFilm) {
            return { isValid: true, dialogConfig: null }
        }
        //Validate duplicate contributions and return result                
        const { result: duplicateResult, duplicates } =
            validateDuplicateContributions(episodeData)

        const duplicateDialogConfig =
            getContributionValidationDialogConfig(duplicateResult)

        if (duplicateDialogConfig) {
            return {
                isValid: false,
                dialogConfig: duplicateDialogConfig,
                duplicates
            }
        }
        //Validate contribution percentages and return result and total percentage
        const { result, totalPercentage } = validateContributionPercentages(episodeData, hasContributionChanges, deleteContributionAvailable)
        const dialogConfig = getContributionValidationDialogConfig(result, totalPercentage)

        return {
            isValid: !dialogConfig,
            dialogConfig
        }
    }

    //Accept and reject for Save cancellation popup

    const accept = () => {
        if (!draftAvailable) {
            navigateTo(`/AudioVisual`)
            return
        }
        try {       
            clearProgrammeDraftResults()
            clearProgrammeEpisodeDraftResults()
            toast.current.show({
                severity: 'info',
                summary: 'Confirmed',
                detail: 'Programme Changes aborted',
                life: 3000
            })       
            navigateTo(`/AudioVisual`)
        } catch (error) {
            console.error('Error in accept function:', error)
        }
    }

    //Cancel Save 

    const showCancelConfirm = (event) => {
        if (draftAvailable) {
            confirmPopup({
                target: event.currentTarget,
                message: 'You have unsaved changes. Do you want to discard them?',
                icon: 'pi pi-info-circle',
                defaultFocus: 'reject',
                acceptClassName: 'p-button-danger',
                accept
            })
        }
        else {
            navigateTo(`/AudioVisual`)
        }
    }

    //Accept Split Buttom Items
    const saveModalItems = [
        {
            label: 'Save',
            icon: 'fa-light fa-check',
            command: async (event) => {
                if (deleteTransmissionAvailable) {
                    confirmPopup({
                        target: event.currentTarget,
                        message: 'You are about to delete Transmissions. Do you want to continue?',
                        icon: 'pi pi-info-circle',
                        acceptClassName: 'p-button-danger',
                        accept: () => handleSaveAfterTransmissionConfirm(event),
                        reject: rejectTransmissionChange
                    })
                } else if (deleteContributionAvailable) {
                    showContributionDeletionConfirmation(event, episodeData,
                        episodeDataOriginal?.contributions || {}, acceptSave)
                } else if (hasContributionChanges) {
                    // Validate contributions BEFORE showing confirmation dialog
                    const contributionValidation = validateContributions()
                    if (!contributionValidation.isValid) {
                        // Show validation error dialog and block save
                        showContributionValidationDialog(contributionValidation.dialogConfig)
                        return
                    }

                    // Check if only Actor/Director contributions changed
                    const onlyNonEligibleChanges = hasOnlyNonEligibleContributionChanges(
                        episodeData,
                        episodeDataOriginal?.contributions || {}
                    )

                    if (onlyNonEligibleChanges) {
                        // Skip reapportionment confirmation for Actor/Director-only changes
                        acceptSave()
                    } else {
                        // Show reapportionment confirmation for eligible contribution changes
                        confirmDialog({
                            message: 'Any undistributed money on this episode will now be re-apportioned. Do you want to continue?',
                            header: 'Confirm Changes',
                            icon: 'pi pi-info-circle',
                            acceptClassName: 'p-button-warning',
                            accept: acceptSave
                        })
                    }
                } else {
                    acceptSave()
                }
            }
        },
        {
            label: 'Save and Exit',
            icon: 'fa-light fa-check',
            command: async (event) => {
                if (deleteTransmissionAvailable) {
                    confirmPopup({
                        target: event.currentTarget,
                        message: 'You are about to delete Transmissions. Do you want to continue?',
                        icon: 'pi pi-info-circle',
                        acceptClassName: 'p-button-danger',
                        accept: () => handleSaveAndExitAfterTransmissionConfirm(event),
                        reject: rejectTransmissionChange
                    })
                } else if (deleteContributionAvailable) {
                    showContributionDeletionConfirmation(event, episodeData,
                        episodeDataOriginal?.contributions || {}, acceptSaveAndExit)
                } else if (hasContributionChanges) {
                    // Validate contributions BEFORE showing confirmation dialog
                    const contributionValidation = validateContributions()
                    if (!contributionValidation.isValid) {
                        // Show validation error dialog and block save
                        showContributionValidationDialog(contributionValidation.dialogConfig)
                        return
                    }

                    // Check if only Actor/Director contributions changed
                    const onlyNonEligibleChanges = hasOnlyNonEligibleContributionChanges(
                        episodeData,
                        episodeDataOriginal?.contributions || {}
                    )

                    if (onlyNonEligibleChanges) {
                        // Skip reapportionment confirmation for Actor/Director-only changes
                        acceptSaveAndExit()
                    } else {
                        // Show reapportionment confirmation for eligible contribution changes
                        confirmDialog({
                            message: 'Any undistributed money on this episode will now be re-apportioned. Do you want to continue?',
                            header: 'Confirm Changes',
                            icon: 'pi pi-info-circle',
                            acceptClassName: 'p-button-warning',
                            accept: acceptSaveAndExit
                        })
                    }
                } else {
                    acceptSaveAndExit()
                }
            }
        }
    ]

    const handleSaveAndExitAfterTransmissionConfirm = (event) => {
        if (deleteContributionAvailable) {
            showContributionDeletionConfirmation(event, episodeData,
                episodeDataOriginal?.contributions || {}, acceptSaveAndExit)
        } else if (hasContributionChanges) {
            // Validate contributions BEFORE showing confirmation dialog
            const contributionValidation = validateContributions()
            if (!contributionValidation.isValid) {
                // Show validation error dialog and block save
                showContributionValidationDialog(contributionValidation.dialogConfig)
                return
            }

            // Check if only Actor/Director contributions changed
            const onlyNonEligibleChanges = hasOnlyNonEligibleContributionChanges(
                episodeData,
                episodeDataOriginal?.contributions || {}
            )

            if (onlyNonEligibleChanges) {
                // Skip reapportionment confirmation for Actor/Director-only changes
                acceptSaveAndExit()
            } else {
                // Show reapportionment confirmation for eligible contribution changes
                confirmDialog({
                    message: 'Any undistributed money on this episode will now be re-apportioned. Do you want to continue?',
                    header: 'Confirm Changes',
                    icon: 'pi pi-info-circle',
                    acceptClassName: 'p-button-warning',
                    accept: acceptSaveAndExit
                })
            }
        } else {
            acceptSaveAndExit()
        }
    }

    const acceptSaveAndExit = async () => {
        const isValid = await validateProgrammeChanges(
            toast,
            dispatch,
            resultsDataForEdit,
            primaryTitle,
            programmeTypeId,
            formatId,
            primaryCountry,
            episodeData,
            episodePrimaryTitle,
            episodeValidationRequired,
            programmeGuid,
            programmeUrl
        )

        if (!isValid) {
            return
        }

        // Validate contributions don't have 0 percentage or minutes (except Actor/Director)
        const zeroValueValidation = validateContributionZeroValues(episodeData)
        if (!zeroValueValidation.isValid) {
            toast.current.show({ severity: 'error', summary: 'Validation Error', detail: zeroValueValidation.message })
            return
        }

        // Validate contributions for films
        const contributionValidation = validateContributions()
        if (!contributionValidation.isValid) {
            showContributionValidationDialog(contributionValidation.dialogConfig)
            return
        }

        if (isNewProgramme) {
            handleCreateTrigger()
        } else {
            handleUpdateTrigger()
        }
        setTimeout(() => {
            navigateTo('/AudioVisual')
        }, 1000)
    }


    //handle update by incrementing the key
    const handleUpdateTrigger = () => {
        dispatch(setProgrammeUpdateStatusAction('idle'))
        setShowDataUpdate(false)
        setDataUpdateKey(Date.now()) // Increment the key to force re-render
        setShowDataUpdate(true)
    }

    //handle update by incrementing the key
    const handleCreateTrigger = () => {
        dispatch(setProgrammeCreateStatusAction('idle'))
        setShowDataCreate(false)
        setDataUpdateKey(Date.now()) // Increment the key to force re-render
        setShowDataCreate(true)
    }

    return (
        <div className="app-footer">
            <SplitButton className='green mr-2 mt-2' label="Save" icon="fa-light fa-check" onClick={splitSave} dropdownIcon="fa-light fa-chevron-down" model={saveModalItems} disabled={(!isNewProgramme && (isNewEpisodeFlag || !draftProgrammeAvailable)) || (isFilm && isContributionEditing)} />
            <Button label="Cancel" icon="fa-light fa-xmark" className="yellow mt-2 mr-3 " raised onClick={showCancelConfirm} ></Button>
        </div>
    )
}
